/**
 * KPI / Overview 全屏 EventWindows 共用数据处理管线：
 * 连续日合并 → 实验类折叠 → 类型排序 → 色条映射。
 * X 轴数字气泡与 EventDetailsPop 仍应使用未合并的原始逐日数据。
 */
import { KPI_DEFAULT_EVENT_TYPE_PRIORITY } from './kpiEventUiUtils';
import { mergeConsecutiveTrendDaysOnEventDataMap } from './mergeConsecutiveTrendDaysOnEventDataMap';
import type {
  EventDataMap,
  EventTypeCollapseConfig,
  ExperimentTypeFixedLabelParams,
  TEventTypeBarColorMapConfig,
} from './types';
import {
  applyEventTypeBarColorMapToEventDataMap,
  applyEventTypeCollapseToEventDataMap,
  applyEventTypeDisplayOrderToEventDataMap,
} from './utils';

export interface IChartEventWindowsPipelineOptions {
  /**
   * 连续自然日同名事件合并。
   * 默认 true（等价 KPI `chartConfig.mergeConsecutiveTrendDaysForChartEventWindows !== false`）。
   */
  mergeConsecutiveTrendDays?: boolean;
  eventTypeCollapseConfig?: EventTypeCollapseConfig;
  showExperimentEventDetailFlat?: boolean;
  isEn?: boolean;
  experimentFixedLabel?: ExperimentTypeFixedLabelParams;
  /**
   * 不传时使用 KPI_DEFAULT_EVENT_TYPE_PRIORITY；
   * 传空数组可跳过排序。
   */
  eventTypeDisplayOrder?: string[];
  eventTypeBarColorMap?: TEventTypeBarColorMapConfig;
  /** 为 false 时跳过色条映射（Overview 已在 buildEventListMapForWindows 上色） */
  applyBarColorMap?: boolean;
}

/**
 * EventWindows 展示用 EventDataMap 管线（与 kpiOverview / EventMetricTrend 一致）。
 */
export function applyChartEventWindowsPipelineToEventDataMap(
  base: EventDataMap | undefined,
  options: IChartEventWindowsPipelineOptions = {},
): EventDataMap | undefined {
  if (!base) {
    return base;
  }

  const {
    mergeConsecutiveTrendDays = true,
    eventTypeCollapseConfig,
    showExperimentEventDetailFlat = false,
    isEn = false,
    experimentFixedLabel,
    eventTypeDisplayOrder,
    eventTypeBarColorMap,
    applyBarColorMap = true,
  } = options;

  let result: EventDataMap | undefined = base;

  if (mergeConsecutiveTrendDays) {
    result = mergeConsecutiveTrendDaysOnEventDataMap(result) ?? result;
  }

  if (!showExperimentEventDetailFlat && eventTypeCollapseConfig) {
    result =
      applyEventTypeCollapseToEventDataMap(
        result,
        eventTypeCollapseConfig,
        isEn,
        experimentFixedLabel,
      ) ?? result;
  }

  const orderList =
    eventTypeDisplayOrder !== undefined
      ? eventTypeDisplayOrder
      : [...KPI_DEFAULT_EVENT_TYPE_PRIORITY];
  if (orderList.length > 0) {
    result =
      applyEventTypeDisplayOrderToEventDataMap(result, orderList) ?? result;
  }

  if (applyBarColorMap && eventTypeBarColorMap) {
    result =
      applyEventTypeBarColorMapToEventDataMap(result, eventTypeBarColorMap) ??
      result;
  }

  return result;
}
